/**
 * Base → Create → Q&A (`/solutions/create/qa`). Upload a questionnaire, confirm
 * the questions the AI found, and get each one answered from the chosen
 * collections only, with citations — as a table, a PDF report and a Word
 * document to edit into the response.
 *
 * `?run=<id>` opens a run, so a result can be linked and survives a reload.
 */

import { useCallback, useState } from "react";
import { Anchor, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { RunList, RunStatus } from "@/features/solutions/shared";
import type { RunDetailOut } from "@/shared/api/solutionRuns";
import { asQaResult } from "./qaTypes";
import { QaResults } from "./QaResults";
import { QaWizard } from "./QaWizard";

export function CreateQaPage() {
  const [params, setParams] = useSearchParams();
  const runId = params.get("run");
  const [run, setRun] = useState<RunDetailOut | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const openRun = useCallback(
    (id: string | null) => {
      setRun(null);
      setParams(id ? { run: id } : {}, { replace: false });
    },
    [setParams],
  );

  const onUpdate = useCallback((next: RunDetailOut) => setRun(next), []);
  const onFinished = useCallback(() => setRefreshKey((k) => k + 1), []);
  const result = run && run.run_id === runId ? asQaResult(run.result) : null;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>Q&amp;A</Title>
        <Anchor component={RouterLink} to="/solutions/create" size="sm">
          All Create tools
        </Anchor>
      </Group>
      <Text c="dimmed" size="sm">
        Upload a questionnaire — a health-authority request, an RFI, a customer questionnaire. The
        questions are pulled out word for word for you to confirm, then each is answered only from
        the collections you choose, with citations to the source.
      </Text>

      <QaWizard
        onCreated={(id) => {
          setRefreshKey((k) => k + 1);
          openRun(id);
        }}
      />

      {runId && (
        <Paper withBorder radius="md" p="lg">
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={4}>{run?.run_id === runId ? run.name : "Run"}</Title>
              <Anchor component="button" size="sm" onClick={() => openRun(null)}>
                Close
              </Anchor>
            </Group>
            <RunStatus runId={runId} onUpdate={onUpdate} onFinished={onFinished} />
            {run?.status === "succeeded" && result && <QaResults run={run} result={result} />}
          </Stack>
        </Paper>
      )}

      <Stack gap="xs">
        <Title order={4}>Your Q&amp;A runs</Title>
        <RunList
          kind="qa"
          selectedRunId={runId}
          onSelect={(r) => openRun(r.run_id)}
          refreshKey={refreshKey}
          emptyText="No Q&A runs yet. Upload a questionnaire above to start."
        />
      </Stack>
    </Stack>
  );
}
